import { useState } from 'react';
import { Lock, LockOpen, ArrowClockwise } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import DragNumberInput from '../ui/drag-number-input';
import type { Layer } from '@spine-workspace/shared';

interface TransformEditorProps {
  layer: Layer;
}

function PropLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-sans text-xs font-medium text-text-secondary w-[52px] shrink-0">
      {children}
    </span>
  );
}

export default function TransformEditor({ layer }: TransformEditorProps) {
  const { updateLayerTransform, pushUndo } = useSceneStore();
  const [lockRatio, setLockRatio] = useState(false);

  function handleScaleX(v: number) {
    if (lockRatio) {
      const ratio = layer.transform.scaleY / (layer.transform.scaleX || 1);
      updateLayerTransform(layer.id, { scaleX: v, scaleY: v * ratio });
    } else {
      updateLayerTransform(layer.id, { scaleX: v });
    }
  }

  function handleScaleY(v: number) {
    if (lockRatio) {
      const ratio = layer.transform.scaleX / (layer.transform.scaleY || 1);
      updateLayerTransform(layer.id, { scaleY: v, scaleX: v * ratio });
    } else {
      updateLayerTransform(layer.id, { scaleY: v });
    }
  }

  return (
    <div className="space-y-2">
      {/* Position */}
      <div className="flex items-center gap-2">
        <PropLabel>Position</PropLabel>
        <DragNumberInput
          label="X"
          labelColorClass="text-error"
          title="X position — drag to scrub"
          value={layer.transform.x}
          onChange={(v) => updateLayerTransform(layer.id, { x: v })}
          onDragStart={pushUndo}
          sensitivity={1}
        />
        <DragNumberInput
          label="Y"
          labelColorClass="text-sage"
          title="Y position — drag to scrub"
          value={layer.transform.y}
          onChange={(v) => updateLayerTransform(layer.id, { y: v })}
          onDragStart={pushUndo}
          sensitivity={1}
        />
      </div>

      {/* Scale */}
      <div className="flex items-center gap-2">
        <PropLabel>Scale</PropLabel>
        <DragNumberInput
          label="X"
          labelColorClass="text-error"
          title="X scale — drag to scrub (↑/↓ to nudge)"
          value={layer.transform.scaleX}
          onChange={handleScaleX}
          onDragStart={pushUndo}
          step={0.01}
          decimals={2}
          sensitivity={0.005}
        />
        <DragNumberInput
          label="Y"
          labelColorClass="text-sage"
          title="Y scale — drag to scrub (↑/↓ to nudge)"
          value={layer.transform.scaleY}
          onChange={handleScaleY}
          onDragStart={pushUndo}
          step={0.01}
          decimals={2}
          sensitivity={0.005}
        />
        <button
          title={lockRatio ? 'Unlock ratio' : 'Lock ratio'}
          onClick={() => setLockRatio((r) => !r)}
          className={`shrink-0 w-[26px] h-[26px] flex items-center justify-center rounded transition-all duration-fast ${
            lockRatio ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          {lockRatio ? <Lock size={12} /> : <LockOpen size={12} />}
        </button>
      </div>

      {/* Rotation */}
      <div className="flex items-center gap-2">
        <PropLabel>
          <span className="flex items-center gap-1"><ArrowClockwise size={11} /> Rotate</span>
        </PropLabel>
        <DragNumberInput
          label="°"
          labelColorClass="text-text-tertiary"
          title="Rotation in degrees — drag to scrub (↑/↓ to nudge)"
          value={layer.transform.rotation}
          onChange={(v) => updateLayerTransform(layer.id, { rotation: v })}
          onDragStart={pushUndo}
          step={1}
          decimals={1}
          sensitivity={0.5}
        />
      </div>

    </div>
  );
}
